import * as React from 'react';
import moment from 'moment';
import { FormAnswerMap, isElementVisible } from 'Src/helpers/form/formRuntime';
import { getSubmissionElementIds } from 'Src/helpers/form/formFlowRuntime';
import { toDownloadFormMediaUrl } from 'Src/helpers/form/formMedia';
import { FormAnswer, FormDefinition, FormElement, FormOption } from 'Src/types/formSchema';

interface Props {
  definition: FormDefinition;
  answers: FormAnswerMap;
  emptyText?: string;
}

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || typeof value === 'undefined' || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const optionLabel = (options: FormOption[] = [], value: unknown): string => {
  const option = options.find(item => item.value === value || String(item.value) === String(value));
  return option ? option.label : String(value ?? '');
};

const formatDate = (value: unknown, withTime: boolean): string => {
  if (!value) return '—';
  const date = moment(String(value));
  if (!date.isValid()) return String(value);
  return date.format(withTime ? 'DD.MM.YYYY / HH:mm' : 'DD.MM.YYYY');
};

const renderUploadedFile = (file: any, index: number): React.ReactNode => {
  const name = String(file?.name || file?.fileName || file?.id || `File ${index + 1}`);
  const url = String(file?.url || file?.href || '');
  const size = Number(file?.size || 0);
  const meta = [file?.mimeType, size > 0 ? `${Math.ceil(size / 1024)} KB` : ''].filter(Boolean).join(' · ');

  return (
    <div key={`${name}_${index}`} className="mb-1">
      {url ? (
        <a href={toDownloadFormMediaUrl(url)} target="_blank" rel="noopener noreferrer" download={name}>
          {name}
        </a>
      ) : (
        <span>{name}</span>
      )}
      {meta ? <span className="text-muted ml-2">{meta}</span> : null}
    </div>
  );
};

const renderAnswerValue = (element: FormElement, answer?: FormAnswer): React.ReactNode => {
  const value = answer?.value;

  if (isEmptyValue(value)) return <span className="text-muted">—</span>;

  switch (element.type) {
    case 'YES_NO':
      return value === true || String(value) === 'true' ? 'Yes' : 'No';
    case 'ACKNOWLEDGEMENT':
      return value === true || String(value) === 'true' ? 'Acknowledged' : 'Not acknowledged';
    case 'SINGLE_CHOICE': {
      const main = value === '__other__'
        ? element.config?.otherLabel || 'Other'
        : optionLabel(element.config?.options, value);
      return answer?.otherValue ? `${main}: ${answer.otherValue}` : main;
    }
    case 'MULTIPLE_CHOICE': {
      const values = Array.isArray(value) ? value : [value];
      const labels = values.map(item =>
        item === '__other__' ? element.config?.otherLabel || 'Other' : optionLabel(element.config?.options, item)
      );
      const text = labels.join(', ');
      return answer?.otherValue ? `${text}: ${answer.otherValue}` : text;
    }
    case 'DATE':
      return formatDate(value, false);
    case 'DATE_TIME':
      return formatDate(value, true);
    case 'RATING':
      return `${value} / ${element.config?.max || 5}`;
    case 'FILE_UPLOAD':
      return Array.isArray(value) ? value.map(renderUploadedFile) : <span>{String(value)}</span>;
    default:
      return Array.isArray(value) ? value.join(', ') : String(value);
  }
};

export const FormAnswersSummary: React.FC<Props> = ({ definition, answers, emptyText = 'No answers were submitted.' }) => {
  const submissionIds = getSubmissionElementIds(definition, answers);
  const questions = definition.elements.filter(
    element => element.kind === 'QUESTION' && submissionIds.has(element.id) && isElementVisible(element, answers)
  );

  if (questions.length === 0) {
    return <div className="alert alert-light border mb-0">{emptyText}</div>;
  }

  return (
    <table className="table table-sm mPreWrap mb-0">
      <tbody>
        {questions.map(element => (
          <tr key={element.id}>
            <td style={{ width: '42%', fontWeight: 600, verticalAlign: 'top' }}>{element.title || 'Question'}</td>
            <td style={{ verticalAlign: 'top' }}>{renderAnswerValue(element, answers[element.id])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

FormAnswersSummary.displayName = 'FormAnswersSummary';